import { ReactNode } from "react";
import styled, { css, DefaultTheme, useTheme } from "styled-components";

const CodePanelChrome = styled.div<{ $fillHeight?: boolean }>`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: ${({ $fillHeight }) => ($fillHeight ? "100%" : "auto")};
    border: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
    border-left: 4px solid ${({ theme }) => theme.colour.COLOUR_MID_DARK};
    background-color: ${({ theme }) => theme.colour.DARK_GREY};
    border-radius: 4px;
    overflow: hidden;
    box-sizing: border-box;
`;

const CodePanelHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.55rem 0.9rem;
    border-bottom: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
    background-color: ${({ theme }) => theme.colour.COLOUR_DARK};
`;

const CodePanelTitle = styled.span`
    font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
    font-size: 0.82rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${({ theme }) => theme.colour.OFF_WHITE};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const CodePanelBody = styled.div<{ $code?: boolean }>`
    flex: 1 1 auto;
    min-height: 0;
    margin: 0;
    background-color: ${({ theme }) => theme.colour.DARK_GREY};

    ${({ $code, theme }) => $code ? css`
        padding: 1rem 1.1rem;
        overflow-x: auto;
        font-family: ${theme.font.FONT_MONO_JETBRAINS};
        font-size: 0.92rem;
        line-height: 1.7;
    ` : css`
        padding: 0;
        overflow: hidden;
    `}
`;

const CodeLine = styled.div`
    white-space: pre;
`;

const CodeToken = styled.span<{ $colour: string }>`
    color: ${({ $colour }) => $colour};
`;

type CodePanelProps = {
    title: string;
    action?: ReactNode;
    children?: ReactNode;
    className?: string;
    fillHeight?: boolean;
    bodyAsCode?: boolean;
};

type ArticleCodeBlockProps = {
    language: string;
    code: string;
    title?: string;
    className?: string;
};

const C_FAMILY_KEYWORDS = [
    "auto", "bool", "break", "case", "char", "class", "const", "constexpr", "continue",
    "default", "delete", "do", "double", "else", "enum", "explicit", "extern", "false",
    "float", "for", "friend", "if", "inline", "int", "namespace", "new", "noexcept",
    "nullptr", "operator", "override", "private", "public", "return", "sizeof", "static",
    "struct", "switch", "template", "this", "throw", "true", "try", "typename", "using",
    "virtual", "void", "while", "uint32_t", "uint64_t", "int32_t", "int64_t", "size_t",
];

const LANGUAGE_KEYWORDS: Record<string, Set<string>> = {
    ts: new Set([
        "as", "async", "await", "break", "case", "catch", "class", "const", "continue",
        "default", "else", "export", "extends", "false", "finally", "for", "from",
        "function", "if", "implements", "import", "in", "interface", "let", "new", "null",
        "of", "return", "switch", "throw", "true", "try", "type", "typeof", "undefined",
        "while", "yield",
    ]),
    bash: new Set([
        "cargo", "cmake", "cosign", "curl", "dig", "docker", "echo", "export", "git",
        "glslc", "helm", "istioctl", "kubectl", "make", "ninja", "nvidia-smi", "openssl",
        "pip", "policy-check", "promote", "psql", "python", "python3", "set", "spirv-opt",
        "spirv-val", "ssh", "trivy", "vulkaninfo", "while", "do", "done", "for", "in",
        "if", "then", "fi", "read", "sudo", "cd", "ls", "cat", "grep", "omh", "dbt",
        "airflow", "vkconfig", "renderdoc", "nsys", "ncu",
    ]),
    python: new Set([
        "and", "as", "assert", "async", "await", "break", "class", "continue", "def",
        "del", "elif", "else", "except", "False", "finally", "for", "from", "global",
        "if", "import", "in", "is", "lambda", "None", "nonlocal", "not", "or", "pass",
        "raise", "return", "True", "try", "while", "with", "yield",
    ]),
    scala: new Set([
        "case", "class", "def", "else", "extends", "false", "final", "for", "if",
        "implicit", "import", "match", "new", "object", "override", "private", "sealed",
        "trait", "true", "val", "var", "with", "yield",
    ]),
    cpp: new Set(C_FAMILY_KEYWORDS),
    glsl: new Set([
        ...C_FAMILY_KEYWORDS,
        "layout", "in", "out", "inout", "uniform", "buffer", "shared", "readonly",
        "writeonly", "vec2", "vec3", "vec4", "ivec2", "ivec3", "ivec4", "uvec2", "uvec3",
        "uvec4", "mat3", "mat4", "uint", "local_size_x", "local_size_y", "local_size_z",
        "std430", "std140", "binding", "set", "push_constant", "discard", "barrier",
        "memoryBarrierShared", "gl_GlobalInvocationID", "gl_LocalInvocationID",
        "gl_WorkGroupID", "gl_LocalInvocationIndex", "version", "main",
    ]),
    yaml: new Set([
        "apiVersion", "kind", "metadata", "spec", "name", "namespace", "labels", "metrics",
        "target", "type", "protocol", "port", "ports", "selector", "template", "containers",
        "image", "resources", "requests", "limits", "ingress", "egress", "rules", "from",
        "to", "podSelector", "matchLabels", "true", "false", "null",
    ]),
    sql: new Set([
        "with", "select", "from", "where", "as", "partition", "by", "order", "over",
        "row_number", "current_date", "interval", "and", "or", "not", "in", "join", "left",
        "inner", "on", "group", "having", "limit", "insert", "into", "values", "update",
        "delete", "create", "table", "index", "primary", "key", "references", "returning",
        "case", "when", "then", "else", "end", "is", "null", "distinct", "union", "all",
        "exists", "between", "like", "asc", "desc", "count", "sum", "avg", "max", "min",
        "coalesce", "now", "cast", "begin", "commit", "rollback", "alter", "add", "column",
        "constraint", "check", "unique", "using", "lateral", "materialized", "view",
        "refresh", "concurrently", "explain", "analyze", "generated", "always", "stored",
        "bigint", "text", "timestamptz", "numeric", "boolean", "jsonb", "uuid",
    ]),
    hcl: new Set([
        "resource", "module", "variable", "output", "provider", "terraform", "data",
        "locals", "true", "false", "for_each", "count", "depends_on", "source", "each",
    ]),
    json: new Set(["true", "false", "null"]),
    text: new Set(),
};

function tokenColour(token: string, language: string, theme: DefaultTheme): string {
    const normalizedToken = token.toLowerCase();
    const keywordSet = LANGUAGE_KEYWORDS[language] ?? new Set<string>();
    const leftTrimmedToken = token.replace(/^\s+/, "");

    if (/^\s+$/.test(token)) {
        return theme.colour.OFF_WHITE;
    }

    if (/^(\/\/|--)/.test(token)) {
        return theme.colour.LIGHT_GREY;
    }

    if ((language === "cpp" || language === "glsl") && leftTrimmedToken.startsWith("#")) {
        return theme.colour.COLOUR_MID;
    }

    if (language !== "cpp" && language !== "glsl" && leftTrimmedToken.startsWith("#")) {
        return theme.colour.LIGHT_GREY;
    }

    if (/^['"`]/.test(token)) {
        return theme.colour.COLOUR_LIGHT;
    }

    if (/^\d/.test(token)) {
        return theme.colour.COLOUR_MID_LIGHT;
    }

    if (keywordSet.has(normalizedToken) || keywordSet.has(token)) {
        return theme.colour.COLOUR_MID;
    }

    if (/^[{}()[\].,:;=<>+\-*/|&!?]+$/.test(token)) {
        return theme.colour.COLOUR_MID_DARK;
    }

    return theme.colour.OFF_WHITE;
}

function isCommentLine(line: string, language: string): boolean {
    const trimmed = line.trimStart();
    if (language === "cpp" || language === "glsl" || language === "ts" || language === "scala" || language === "hcl") {
        return trimmed.startsWith("//") || trimmed.startsWith("/*") || trimmed.startsWith("*");
    }
    if (language === "sql") {
        return trimmed.startsWith("--");
    }
    if (language === "bash" || language === "python" || language === "yaml") {
        return trimmed.startsWith("#");
    }
    return false;
}

export function tokenizeCodeLine(line: string): string[] {
    return line.match(/\/\/.*$|--.*$|"(?:\\.|[^"])*"|'(?:\\.|[^'])*'|`(?:\\.|[^`])*`|\b\d+(?:\.\d+)?\b|\b[A-Za-z_][\w-]*\b|[{}()[\].,:;=<>+\-*/|&!?]+|\s+|./g) ?? [];
}

export function HighlightedLine({ line, language }: { line: string; language: string }) {
    const theme = useTheme();

    if (isCommentLine(line, language)) {
        return <CodeLine><CodeToken $colour={theme.colour.LIGHT_GREY}>{line}</CodeToken></CodeLine>;
    }

    return (
        <CodeLine>
            {tokenizeCodeLine(line).map((token, tokenIndex) => (
                <CodeToken key={tokenIndex} $colour={tokenColour(token, language, theme)}>
                    {token}
                </CodeToken>
            ))}
        </CodeLine>
    );
}

export function CodePanel({
    title,
    action,
    children,
    className,
    fillHeight = false,
    bodyAsCode = false,
}: CodePanelProps) {
    return (
        <CodePanelChrome className={className} $fillHeight={fillHeight}>
            <CodePanelHeader>
                <CodePanelTitle>{title}</CodePanelTitle>
                {action}
            </CodePanelHeader>
            <CodePanelBody as={bodyAsCode ? "pre" : "div"} $code={bodyAsCode}>
                {children}
            </CodePanelBody>
        </CodePanelChrome>
    );
}

export function ArticleCodeBlock({ language, code, title, className }: ArticleCodeBlockProps) {
    return (
        <CodePanel title={title ? `${language} · ${title}` : language} className={className} bodyAsCode>
            {code.split("\n").map((line, lineIndex) => (
                <HighlightedLine key={lineIndex} line={line} language={language} />
            ))}
        </CodePanel>
    );
}
